import { useState } from 'react';

export default function PrivateParties() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const packages = [
    {
      name: 'Starter',
      price: '$299',
      features: [
        'Up to 15 guests',
        '2 hours of arcade access',
        'Reserved gaming area',
        '1 pitcher of beer or soda',
        'Basic party setup'
      ],
      color: 'cyan'
    },
    {
      name: 'Classic',
      price: '$599',
      features: [
        'Up to 30 guests',
        '3 hours of arcade access',
        'Private party room',
        '3 pitchers + appetizer platter',
        'Dedicated party host',
        'Custom tournament setup'
      ],
      color: 'pink',
      popular: true
    },
    {
      name: 'Boss Mode',
      price: '$999',
      features: [
        'Up to 50 guests',
        '4 hours of arcade access',
        'Full venue buyout option',
        'Open bar + full catering',
        'DJ or karaoke setup',
        'Custom decorations',
        'Photo booth access'
      ],
      color: 'orange'
    }
  ];

  const faqs = [
    {
      question: 'What\'s the minimum number of guests?',
      answer: 'Our Starter package begins at 10 guests, but we can accommodate groups of any size with custom packages.'
    },
    {
      question: 'Can we reserve specific games?',
      answer: 'Absolutely! We can reserve your favorite arcade cabinets and set up custom tournaments for your group.'
    },
    {
      question: 'Are there food and drink minimums?',
      answer: 'Each package includes food and drinks. Additional items can be added à la carte based on your needs.'
    },
    {
      question: 'What\'s your age policy for parties?',
      answer: 'All ages welcome until 9 PM. After 9 PM, it\'s 21+ only. Private buyouts can accommodate all-ages events at any time.'
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="parties" className="relative py-24 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c] to-[#0a1628]">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-orange-400 mb-4">
            Level Up Your Next Party
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 via-pink-500 to-orange-500 mx-auto mb-6"></div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Birthdays, corporate events, bachelor parties, or just because — we make every celebration legendary
          </p>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`relative bg-gradient-to-br from-[#0d1f3c] to-[#0a1628] rounded-xl p-8 border-2 ${
                pkg.popular ? 'border-pink-500 shadow-[0_0_40px_rgba(236,72,153,0.4)]' : 'border-cyan-500/20'
              } hover:border-${pkg.color}-500/50 transition-all duration-300 hover:-translate-y-2`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-pink-500 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  MOST POPULAR
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className={`text-3xl font-black text-${pkg.color}-400 mb-2`}>{pkg.name}</h3>
                <div className="text-5xl font-black text-white mb-2">{pkg.price}</div>
                <div className="text-gray-400 text-sm">per event</div>
              </div>

              <ul className="space-y-4 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <i className={`ri-checkbox-circle-fill text-${pkg.color}-400 mr-3 mt-1 flex-shrink-0`}></i>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToSection('contact')}
                className={`w-full py-3 bg-gradient-to-r from-${pkg.color}-500 to-${pkg.color}-600 text-white font-bold rounded-lg hover:shadow-[0_0_20px_rgba(34,211,238,0.5)] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer`}
              >
                Request Booking
              </button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-3xl font-black text-center text-cyan-400 mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#0d1f3c] to-[#0a1628] rounded-xl border border-cyan-500/20 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-cyan-500/5 transition-colors duration-300 cursor-pointer"
                >
                  <span className="text-white font-bold pr-4">{faq.question}</span>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-cyan-400 text-xl flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
